import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class MacroService {
    /**
     * Stato interno delle macro.
     * Chiave = codice macro (es. "A"), valore = stringa sostitutiva.
     *
     * Esempio di utilizzo nel testo: "Ciao ${NAME}" con macroMap { NAME: "Mario" }.
     */
    private _macroMap$ = new BehaviorSubject<Record<string, string>>({});

    /**
     * Stream pubblico della macroMap.
     * Emissioni duplicate vengono soppresse tramite shallowEqual (confronto per chiavi/valori).
     */
    public macroMap$: Observable<Record<string, string>> = this._macroMap$.pipe(
        distinctUntilChanged((a, b) => this.shallowEqual(a, b))
    );

    /**
     * Snapshot sincrono dell'ultima macroMap emessa.
     * Utile in contesti non reattivi (es. build sincrono di un modello).
     */
    get macroMap(): Record<string, string> {
        return this._macroMap$.value;
    }

    /**
     * Sostituzione totale della macroMap.
     * - Sovrascrive completamente lo stato precedente
     * - Clona l'oggetto in ingresso per evitare mutazioni esterne
     */
    setMacros(macros: Record<string, string>): void {
        this._macroMap$.next({ ...macros });
    }

    /**
     * Merge/patch incrementale della macroMap.
     * - Aggiunge/aggiorna le chiavi presenti in `patch`
     * - Mantiene inalterate le altre chiavi
     */
    patchMacros(patch: Record<string, string>): void {
        this._macroMap$.next({ ...this._macroMap$.value, ...patch });
    }

    /**
     * Rimuove una singola macro (se presente).
     */
    removeMacro(key: string): void {
        const next = { ...this._macroMap$.value };
        delete next[key];
        this._macroMap$.next(next);
    }

    /**
     * Pulisce tutte le macro (reset a mappa vuota).
     */
    clear(): void {
        this._macroMap$.next({});
    }

    // =========================================================
    // METODI DI UTILITÀ
    // =========================================================

    /**
     * Se viene passata una macroMap esplicita usa quella,
     * altrimenti usa lo stato interno corrente (snapshot).
     *
     * Permette nei vari metodi di utilizzare una macroMap passata e in caso non venga passato nulla di usare lo snapshot interno
     */
    private resolveMap(macroMap?: Record<string, string>): Record<string, string> {
        return macroMap ?? this._macroMap$.value;
    }

    /**
     * Estrae i codici macro da un testo.
     *
     * Esempio:
     * - input:  "${A} x ${B} - ${A}"
     * - output: ["A", "B"]  (unici, nell'ordine di prima apparizione)
     */
    getMacroFromText(text: string): string[] {
        const macroRegex = /\$\{([^}]+)\}/g;

        const out: string[] = [];
        const seen = new Set<string>();

        let m: RegExpExecArray | null;
        while ((m = macroRegex.exec(text)) !== null) {
            const code = m[1].trim();
            if (!seen.has(code)) {
                seen.add(code);
                out.push(code);
            }
        }

        return out;
    }

    /**
     * Verifica se un codice macro esiste nella macroMap (passata o interna).
     * Il controllo è per "chiave presente", non per valore non-vuoto.
     */
    existsMacroInMacroMap(macroCode: string, macroMap?: Record<string, string>): boolean {
        const map = this.resolveMap(macroMap);
        const k = macroCode.trim();
        return Object.prototype.hasOwnProperty.call(map, k);
    }

    /**
     * Sostituisce le macro nel testo usando la macroMap (passata o interna).
     *
     * Comportamento in caso di macro mancante:
     * - lascia invariato il token "${X}"
     *
     * Nota sul valore:
     * - se la chiave esiste ma il valore è null/undefined, viene sostituito con stringa vuota.
     */
    replaceMacroInText(text: string, macroMap?: Record<string, string>): string {
        const map = this.resolveMap(macroMap);
        const macroRegex = /\$\{([^}]+)\}/g;

        return text.replace(macroRegex, (m, key: string) => {
            const k = key.trim();
            return Object.prototype.hasOwnProperty.call(map, k) ? String(map[k] ?? '') : m;
        });
    }

    /**
     * Confronto "shallow" tra due mappe string->string:
     * - stesso numero di chiavi
     * - per ogni chiave in 'a', stesso valore in 'b'
     *
     * Usato per ridurre emissioni inutili su macroMap$.
     */
    shallowEqual(a: Record<string, string>, b: Record<string, string>): boolean {
        if (a === b) return true;
        const ak = Object.keys(a);
        const bk = Object.keys(b);
        if (ak.length !== bk.length) return false;
        for (const k of ak) if (a[k] !== b[k]) return false;
        return true;
    }
}
